import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import Avatar from "../../../components/ui/Avatar";
import { useConfig } from "../../../context/ConfigContext";
import { useTestimonials } from "../../../hooks/useTestimonials";
import { formatNumMil, formatNumKgT } from "components/utils/format";

const CustomerSuccessCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { testimonials, loading } = useTestimonials();
  const config = useConfig();
  const { COMPANY_NAME, FACT_CO2_PROMEDIO_ANUAL, KWH_MES_PANEL } = config;

  // Fallback/Default stories if Firestore is empty or loading initially
  const defaultStories = [
    {
      id: 1,
      name: "Gilberto Marroquin",
      location: "Mixco, Guatemala",
      image: "",
      systemSize: 6,
      monthlyBefore: "Q 500.00",
      monthlyAfter: "Q 85.00",
      monthlySavings: "Q 415.00",
      testimonial: `Desde que instalamos los paneles solares con Solar Valley, nuestra factura de luz se redujo un 90%. 
      El equipo fue muy profesional y el sistema funciona perfectamente. ¡Recomiendo Solar Valley a todas las familias guatemaltecas!`,
      installDate: "Febrero 2024",
    },
  ];

  const customerStories =
    testimonials && testimonials.length > 0 ? testimonials : defaultStories;

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % customerStories?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, customerStories?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % customerStories?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + customerStories?.length) % customerStories?.length
    );
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentStory = customerStories?.[currentSlide];

  return (
    <section className="py-16 bg-gradient-to-r from-muted/30 to-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Users" size={16} />
            <span>Historias de Éxito</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Familias Guatemaltecas Ahorrando con {COMPANY_NAME}
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Descubre cómo nuestros clientes han transformado sus hogares y
            reducido sus facturas eléctricas
          </p>
        </div>

        <div className="relative">
          {/* Main Carousel */}
          <div className="bg-white rounded-2xl solar-shadow-elevated overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Customer Info */}
              <div className="p-8 lg:p-12">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="relative">
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-white flex items-center justify-center">
                      <Icon name="Check" size={12} className="text-white" />
                    </div>
                    <Avatar
                      src={currentStory?.image}
                      name={currentStory?.name}
                      alt={currentStory?.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white solar-shadow-card"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary">
                      {currentStory?.name}
                    </h3>
                    <p className="text-text-secondary flex items-center">
                      <Icon name="MapPin" size={14} className="mr-1" />
                      {currentStory?.location}
                    </p>
                  </div>
                </div>

                <blockquote className="text-text-primary mb-6 text-lg leading-relaxed">
                  "{currentStory?.testimonial || currentStory?.quote}"
                </blockquote>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-text-secondary mb-1">
                      Sistema
                    </div>
                    <div className="font-semibold text-primary">
                      {currentStory?.systemSize > 0 ? `${currentStory?.systemSize} Paneles` : "N/A"}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-text-secondary mb-1">
                      Instalación
                    </div>
                    <div className="font-semibold text-secondary">
                      {currentStory?.installDate}
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Link to="/proyectos">
                    <Button
                      variant="outline"
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Ver nuestros proyectos
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Savings Breakdown */}
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-8 lg:p-12">
                <h4 className="text-xl font-semibold text-text-primary mb-6 text-center">
                  Impacto Real de la Instalación
                </h4>

                {/* Before/After Comparison */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg solar-shadow-card">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-error rounded-full"></div>
                      <span className="text-text-secondary">
                        Factura Anterior
                      </span>
                    </div>
                    <span className="font-bold text-error">
                      {currentStory?.monthlyBefore}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white rounded-lg solar-shadow-card">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-success rounded-full"></div>
                      <span className="text-text-secondary">
                        Factura Actual
                      </span>
                    </div>
                    <span className="font-bold text-success">
                      {currentStory?.monthlyAfter}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-primary text-primary-foreground rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="TrendingDown" size={16} />
                      <span className="font-medium">Ahorro Mensual</span>
                    </div>
                    <span className="font-bold text-xl">
                      {currentStory?.monthlySavings || currentStory?.savings}
                    </span>
                  </div>
                </div>

                {/* Environmental Impact */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg solar-shadow-card">
                    <Icon
                      name="Zap"
                      size={24}
                      className="mx-auto mb-2 text-accent"
                    />
                    <div className="text-sm text-text-secondary mb-1">
                      Energía Anual
                    </div>
                    <div className="font-bold text-accent">
                      {formatNumMil(KWH_MES_PANEL * currentStory?.systemSize * 12)}Wh
                    </div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg solar-shadow-card">
                    <Icon
                      name="Leaf"
                      size={24}
                      className="mx-auto mb-2 text-success"
                    />
                    <div className="text-sm text-text-secondary mb-1">
                      CO₂ Reducido Anual
                    </div>
                    <div className="font-bold text-success">
                      {formatNumKgT((KWH_MES_PANEL * currentStory?.systemSize * 12) * FACT_CO2_PROMEDIO_ANUAL)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={prevSlide}
              className="p-3 bg-white rounded-full solar-shadow-card hover:bg-muted solar-transition"
              aria-label="Historia anterior"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>

            {/* Slide Indicators */}
            <div className="flex space-x-2">
              {customerStories?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full solar-transition ${index === currentSlide
                    ? "bg-primary"
                    : "bg-muted hover:bg-primary/50"
                    }`}
                  aria-label={`Ir a historia ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="p-3 bg-white rounded-full solar-shadow-card hover:bg-muted solar-transition"
              aria-label="Siguiente historia"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerSuccessCarousel;
